import React, { useEffect, useMemo, useState } from "react";
import {
    Autocomplete,
    Box,
    TextField,
} from "@mui/material";
import {
    DataGridPro,
    GridToolbarContainer,
    frFR,
    nlNL,
    deDE,
    esES,
    ptBR,
    enUS,
} from "@mui/x-data-grid-pro";
import { t } from "react-i18nify";
import { useSelector } from "react-redux";
import { fetchData } from "../../js/utils/backend";
import { ScreenLoading } from "../Widgets/CustomLoadings";
import { SearchInTable } from "../DataGrid/cst_DataGrid";

const granularityOptions = ["Monthly", "Quarterly", "Yearly"];

// multiple entities can share a study type / category with the same name but different ObjectIDs: group them so the dropdown shows each name once
const groupOptionsByValue = options => {
    const groups = new Map();
    options.forEach(option => {
        const existing = groups.get(option.Value);
        if (existing) {
            existing.ObjectIDs.push(option.ObjectID);
        } else {
            groups.set(option.Value, { Value: option.Value, ObjectIDs: [option.ObjectID] });
        }
    });
    return Array.from(groups.values());
};

const localeByLanguage = {
    ENG: "en-US",
    FRA: "fr-FR",
    SPA: "es-ES",
    POR: "pt-PT",
    NLD: "nl-NL",
    DEU: "de-DE",
};

const dataGridLocalisation = {
    ENG: enUS,
    FRA: frFR,
    NLD: nlNL,
    DEU: deDE,
    SPA: esES,
    POR: ptBR,
};

const buildParamURL = ({ entityIds, studyTypeIds, categoryIds, granularity }) => {
    const params = new URLSearchParams();
    if (entityIds.length) params.set("EntityIds", entityIds.join(","));
    if (studyTypeIds.length) params.set("StudyTypeIds", studyTypeIds.join(","));
    if (categoryIds.length) params.set("CategoryIds", categoryIds.join(","));
    params.set("Granularity", granularity);
    return params.toString();
};

/** Displays EDD sampling frequency stats (# of samples per period), filtered by entity / study type / study category, grouped Monthly/Quarterly/Yearly. DOC*/
const EDDStatsView = () => {
    const token = useSelector(state => state.auth.Token);
    const locale = useSelector(state => state.i18n.locale);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [entityIds, setEntityIds] = useState([]);
    const [studyTypeIds, setStudyTypeIds] = useState([]);
    const [categoryIds, setCategoryIds] = useState([]);
    const [granularity, setGranularity] = useState("Monthly");

    useEffect(() => {
        let isSubscribed = true;
        const fetching = async () => {
            setLoading(true);
            const paramURL = buildParamURL({ entityIds, studyTypeIds, categoryIds, granularity });
            const result = await fetchData("EDDStats", token, "List", null, paramURL);
            if (isSubscribed) {
                setData(result?.data ?? null);
                setLoading(false);
            }
        };
        fetching();
        return () => (isSubscribed = false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, entityIds, studyTypeIds, categoryIds, granularity]);

    // drop selections that are no longer valid options once the cascading filters change server-side
    // (keep the same array reference when nothing changes, otherwise this re-triggers the fetch effect forever)
    useEffect(() => {
        if (!data) return;
        const validStudyTypeIds = new Set(data.StudyTypes.map(x => x.ObjectID));
        const validCategoryIds = new Set(data.Categories.map(x => x.ObjectID));
        setStudyTypeIds(ids => {
            const filtered = ids.filter(id => validStudyTypeIds.has(id));
            return filtered.length === ids.length ? ids : filtered;
        });
        setCategoryIds(ids => {
            const filtered = ids.filter(id => validCategoryIds.has(id));
            return filtered.length === ids.length ? ids : filtered;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.StudyTypes, data?.Categories]);

    const studyTypeOptions = useMemo(() => groupOptionsByValue(data?.StudyTypes ?? []), [data?.StudyTypes]);
    const categoryOptions = useMemo(() => groupOptionsByValue(data?.Categories ?? []), [data?.Categories]);

    const [searchText, setSearchText] = useState("");

    const getPeriodLabel = period => {
        if (data.Granularity !== "Monthly") return period.Label;
        const date = new Date(period.Key);
        return date.toLocaleString(localeByLanguage[locale] ?? "en-US", { month: "short" });
    };

    const columns = useMemo(() => {
        if (!data) return [];
        const textColumns = [
            ...(data.ShowEntityFilter ? [{ field: "EntityName", headerName: t("field.Entity"), flex: 1, minWidth: 140 }] : []),
            { field: "StudyName", headerName: t("field.Study"), flex: 1, minWidth: 140 },
            { field: "StudyTypeName", headerName: t("field.StudyType"), flex: 1, minWidth: 140 },
            { field: "CategoryName", headerName: t("tree.StudyCategory"), flex: 1, minWidth: 140 },
        ];
        const periodColumns = data.Periods.map(period => ({
            field: period.Key,
            headerName: period.GroupLabel && period.GroupLabel !== getPeriodLabel(period)
                ? `${getPeriodLabel(period)}-${period.GroupLabel}`
                : getPeriodLabel(period),
            headerAlign: "center",
            align: "center",
            minWidth: 90,
            type: "number",
            renderCell: params => (
                <Box
                    sx={params.value === 0 ? { color: "error.main", fontWeight: "bold" } : undefined}
                >
                    {params.value}
                </Box>
            ),
        }));
        return textColumns.concat(periodColumns);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, locale]);

    const rows = useMemo(() => {
        if (!data) return [];
        return data.Rows.map(row => ({
            id: row.StudyID,
            EntityName: row.EntityName,
            StudyName: row.StudyName,
            StudyTypeName: row.StudyTypeName,
            CategoryName: row.CategoryName,
            ...Object.fromEntries(data.Periods.map(period => [period.Key, row.Values[period.Key] ?? 0])),
        }));
    }, [data]);

    const filteredRows = useMemo(() => {
        if (!searchText) return rows;
        const search = searchText.toLowerCase();
        return rows.filter(row =>
            [row.EntityName, row.StudyName, row.StudyTypeName, row.CategoryName].some(value =>
                value?.toLowerCase().includes(search)
            )
        );
    }, [rows, searchText]);

    const StatsToolbar = () => (
    <GridToolbarContainer sx={{ justifyContent: "flex-end", p: 1 }}>
        <SearchInTable searchText={searchText} setSearchText={setSearchText} />
    </GridToolbarContainer>
    );

    if (loading && !data) {
        return <ScreenLoading />;
    }

    if (!data) {
        return null;
    }

    return (
        <Box>
            <Box
                sx={{
                    typography: "h3",
                    pt: 6,
                    pl: 5,
                    pb: 2,
                    display: "flex",
                    alignItems: "baseline",
                }}
            >
                {t("tree.EDDStats")}
            </Box>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: 2,
                    mx: "40px",
                }}
            >
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {data.ShowEntityFilter && (
                        <Autocomplete
                            multiple
                            size="small"
                            sx={{ minWidth: 240 }}
                            options={data.Entities}
                            getOptionLabel={option => option.Value}
                            isOptionEqualToValue={(option, value) => option.ObjectID === value.ObjectID}
                            value={data.Entities.filter(x => entityIds.includes(x.ObjectID))}
                            onChange={(event, values) => {
                                // study type / category options depend on the selected entities, so reset them to avoid hiding studies from newly added entities
                                setEntityIds(values.map(x => x.ObjectID));
                                setStudyTypeIds([]);
                                setCategoryIds([]);
                            }}
                            renderInput={params => (
                                <TextField {...params} label={t("field.Entity")} placeholder={t("common.SelectAll")} />
                            )}
                        />
                    )}
                    <Autocomplete
                        multiple
                        size="small"
                        sx={{ minWidth: 240 }}
                        options={studyTypeOptions}
                        getOptionLabel={option => option.Value}
                        isOptionEqualToValue={(option, value) => option.Value === value.Value}
                        value={studyTypeOptions.filter(x => x.ObjectIDs.some(id => studyTypeIds.includes(id)))}
                        onChange={(event, values) => setStudyTypeIds(values.flatMap(x => x.ObjectIDs))}
                        renderInput={params => (
                            <TextField {...params} label={t("field.StudyType")} placeholder={t("common.SelectAll")} />
                        )}
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        sx={{ minWidth: 240 }}
                        options={categoryOptions}
                        getOptionLabel={option => option.Value}
                        isOptionEqualToValue={(option, value) => option.Value === value.Value}
                        value={categoryOptions.filter(x => x.ObjectIDs.some(id => categoryIds.includes(id)))}
                        onChange={(event, values) => setCategoryIds(values.flatMap(x => x.ObjectIDs))}
                        renderInput={params => (
                            <TextField {...params} label={t("tree.StudyCategory")} placeholder={t("common.SelectAll")} />
                        )}
                    />
                    <Autocomplete
                        disableClearable
                        size="small"
                        sx={{ minWidth: 160 }}
                        options={granularityOptions}
                        getOptionLabel={option => t(`tree.${option}`)}
                        value={granularity}
                        onChange={(event, value) => value && setGranularity(value)}
                        renderInput={params => <TextField {...params} label={t("tree.Granularity")} />}
                    />
                </Box>
            </Box>
            <DataGridPro
                autoHeight
                rowHeight={38}
                columns={columns}
                rows={filteredRows}
                disableColumnMenu
                disableColumnSelector
                disableSelectionOnClick
                loading={loading}
                pagination
                rowsPerPageOptions={[10, 20, 50, 100]}
                initialState={{ pagination: { pageSize: 10 } }}
                localeText={dataGridLocalisation[locale]?.components.MuiDataGrid.defaultProps.localeText}
                components={{ Toolbar: StatsToolbar }}
            />
        </Box>
    );
};

export default EDDStatsView;
